import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Empty, ErrorBox, Loading } from "../../components/Loading";
import { setBackButton, hapticImpact } from "../../lib/tg";
import { useAuth } from "../../AuthContext";
import { useAddCoach, useRemoveCoach, useRoster, type RosterItem } from "../../lib/queries";
import { AddBalanceSheet } from "../../components/AddBalanceSheet";
import { InviteSheet } from "../../components/InviteSheet";
import { useToast } from "../../components/Toast";
import { fmtDate } from "../../lib/format";

type SortMode = "name" | "balance" | "lastSeen";

export default function People() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const addCoach = useAddCoach();
  const removeCoach = useRemoveCoach();
  const toast = useToast();
  useEffect(() => setBackButton(() => navigate("/t")), [navigate]);

  const rosterQ = useRoster();
  const [filter, setFilter] = useState<"mine" | "all">("mine");
  const [sort, setSort] = useState<SortMode>("name");
  const [selectedParticipant, setSelectedParticipant] = useState<{ id: string; name: string; kind: "single" | "pack" } | null>(null);
  const [inviting, setInviting] = useState(false);

  const items = useMemo<RosterItem[]>(() => {
    const list = (rosterQ.data ?? []).slice();
    const filtered = filter === "mine"
      ? list.filter((p) => p.coaches.some((c) => c.id === user?.trainer_id))
      : list;
    filtered.sort((a, b) => {
      if (sort === "name") return a.name.localeCompare(b.name, "uk");
      if (sort === "balance") {
        const sa = a.balances.reduce((acc, x) => acc + x.remaining, 0);
        const sb = b.balances.reduce((acc, x) => acc + x.remaining, 0);
        return sa - sb;  // low balance first (alerts trainer who needs to top up)
      }
      // lastSeen: most recent first
      if (!a.last_attended_at) return 1;
      if (!b.last_attended_at) return -1;
      return b.last_attended_at.localeCompare(a.last_attended_at);
    });
    return filtered;
  }, [rosterQ.data, filter, sort, user?.trainer_id]);

  if (rosterQ.isLoading) return <Loading />;
  if (rosterQ.error) return <ErrorBox message={(rosterQ.error as Error).message} />;

  return (
    <div className="p-4 space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Учні</h1>
        <button className="text-tg-link text-sm" onClick={() => setInviting(true)}>
          + Тренер
        </button>
      </header>

      <div className="flex flex-wrap gap-2">
        <div className="inline-flex rounded-full bg-tg-secondary p-0.5 text-sm">
          <button
            className={`px-3 py-1 rounded-full transition ${filter === "mine" ? "bg-tg-bg shadow" : "text-tg-hint"}`}
            onClick={() => setFilter("mine")}
          >Мої</button>
          <button
            className={`px-3 py-1 rounded-full transition ${filter === "all" ? "bg-tg-bg shadow" : "text-tg-hint"}`}
            onClick={() => setFilter("all")}
          >Усі клубу</button>
        </div>
        <div className="inline-flex rounded-full bg-tg-secondary p-0.5 text-sm">
          <button className={`px-3 py-1 rounded-full ${sort === "name" ? "bg-tg-bg shadow" : "text-tg-hint"}`} onClick={() => setSort("name")}>Ім'я</button>
          <button className={`px-3 py-1 rounded-full ${sort === "balance" ? "bg-tg-bg shadow" : "text-tg-hint"}`} onClick={() => setSort("balance")}>Залишок</button>
          <button className={`px-3 py-1 rounded-full ${sort === "lastSeen" ? "bg-tg-bg shadow" : "text-tg-hint"}`} onClick={() => setSort("lastSeen")}>Активність</button>
        </div>
      </div>

      {items.length === 0 ? (
        <Empty label={filter === "mine" ? "У тебе ще немає прив'язаних учнів." : "У клубі поки немає учнів."} />
      ) : (
        <ul className="space-y-2">
          {items.map((p) => {
            const isMyTrainee = p.coaches.some((c) => c.id === user?.trainer_id);
            const totalBalance = p.balances.reduce((acc, b) => acc + b.remaining, 0);
            return (
              <li key={p.id} className="card">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <Link to={`/t/participant/${p.id}`} className="text-tg-link font-medium">
                      {p.name}{p.birth_year ? ` (${p.birth_year})` : ""} →
                    </Link>
                    <div className="text-tg-hint text-xs mt-0.5">
                      Батько: {p.parent_name}
                      {p.last_attended_at && <> · був {fmtDate(p.last_attended_at)}</>}
                      {!p.last_attended_at && <> · ще не був</>}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <button
                      className="btn-secondary text-xs whitespace-nowrap"
                      onClick={() => setSelectedParticipant({ id: p.id, name: p.name, kind: "single" })}
                    >+ Разове</button>
                    <button
                      className="btn-secondary text-xs whitespace-nowrap"
                      onClick={() => setSelectedParticipant({ id: p.id, name: p.name, kind: "pack" })}
                    >+ Абонемент</button>
                  </div>
                </div>

                <div className="flex flex-wrap gap-1.5 mt-2">
                  {p.balances.length === 0 ? (
                    <span className="chip text-xs text-tg-hint">баланс 0</span>
                  ) : p.balances.map((b) => {
                    const debt = b.remaining < 0;
                    const cls = debt
                      ? "bg-amber-500/15 text-amber-700"
                      : b.remaining === 0
                        ? "text-tg-hint"
                        : "bg-green-500/10 text-green-700";
                    return (
                      <span key={b.slug} className={`chip text-xs ${cls}`}>
                        {b.emoji} {b.remaining}{debt ? " 🔻" : ""}
                      </span>
                    );
                  })}
                  {totalBalance <= 0 && p.upcoming_count > 0 && (
                    <span className="chip text-xs bg-amber-500/10 text-amber-700">
                      {p.upcoming_count} майб. без оплати
                    </span>
                  )}
                </div>

                <button
                  className="text-xs text-tg-link mt-2"
                  onClick={() => {
                    hapticImpact();
                    if (isMyTrainee) {
                      removeCoach.mutate(
                        { participant_id: p.id, trainer_id: user!.trainer_id! },
                        { onError: (e) => toast.error((e as Error).message) },
                      );
                    } else {
                      addCoach.mutate(
                        { participant_id: p.id },
                        { onError: (e) => toast.error((e as Error).message) },
                      );
                    }
                  }}
                >{isMyTrainee ? "✓ Я тренер (відкріпити)" : "+ Стати тренером"}</button>
              </li>
            );
          })}
        </ul>
      )}

      {selectedParticipant && (
        <AddBalanceSheet
          participant={selectedParticipant}
          kind={selectedParticipant.kind}
          onClose={() => setSelectedParticipant(null)}
        />
      )}

      {inviting && (
        <InviteSheet role="trainer" onClose={() => setInviting(false)} />
      )}
    </div>
  );
}
